import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  List,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { documentService } from '@/services/document.service';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { CANDIDATE_SOURCES, recruitErrorMessage } from './shared/recruitUi';

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const CandidateFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [cv, setCv] = useState<UploadFile[]>([]);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);

  const { data: existing, isLoading } = useQuery({
    queryKey: hrmsKeys.candidate(id || ''),
    queryFn: () => hrmsService.getCandidate(id!),
    enabled: isEdit,
  });
  const { data: tagOptions = [] } = useQuery({ queryKey: hrmsKeys.candidateTags(), queryFn: () => hrmsService.listCandidateTags() });

  useEffect(() => {
    if (existing) form.setFieldsValue({ ...existing, experience_years: existing.experience_years != null ? Number(existing.experience_years) : undefined });
  }, [existing, form]);

  const email = Form.useWatch('email', form);
  const phone = Form.useWatch('phone', form);
  const source = Form.useWatch('source', form);
  const isInternal = Form.useWatch('is_internal', form);
  const probe = useDebounced({ email: email?.trim() || undefined, phone: phone?.trim() || undefined }, 500);
  const { data: duplicates = [] } = useQuery({
    queryKey: ['hrms', 'candidate-dup', probe, id],
    queryFn: () => hrmsService.findDuplicateCandidates({ ...probe, exclude_id: id }),
    enabled: Boolean(probe.email || (probe.phone && probe.phone.replace(/\D/g, '').length >= 7)),
  });
  const strongDup = useMemo(
    () => duplicates.filter((d: any) => d.match_on?.includes('email') || d.match_on?.includes('phone')),
    [duplicates]
  );

  const save = async () => {
    try {
      const v = await form.validateFields();
      if (!isEdit && strongDup.length && !confirmDuplicate) {
        message.warning('This looks like an existing candidate. Open that record, or tick "Create anyway".');
        return;
      }
      setSaving(true);
      const saved = isEdit
        ? await hrmsService.updateCandidate(id!, v)
        : await hrmsService.createCandidate({ ...v, force: confirmDuplicate });
      const file = cv[0]?.originFileObj as File | undefined;
      if (file) {
        try {
          const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase() : 'File';
          await documentService.uploadDocument(file, {
            entity_type: 'HR_CAND',
            entity_id: saved.id,
            document_name: `CV — ${saved.full_name}`,
            document_type: ext,
            document_category: 'CV',
          } as any);
        } catch (e: any) {
          message.warning(`Candidate saved, but the CV upload failed: ${recruitErrorMessage(e)}`);
        }
      }
      message.success(isEdit ? 'Candidate updated' : `Candidate ${saved.candidate_number} created`);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.candidates() });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.candidate(saved.id) });
      navigate(`/hr/candidates/${saved.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(recruitErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Candidate" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? `Edit ${existing?.candidate_number || 'candidate'}` : 'New candidate'}
          subtitle="Talent pool record — one per person; applications link to it"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(isEdit ? `/hr/candidates/${id}` : '/hr/candidates')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
                Save
              </Button>
            </Space>
          }
        />
      }
    >
      {strongDup.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16, maxWidth: 960 }}
          message="Possible duplicate"
          description={
            <>
              <List
                size="small"
                dataSource={strongDup}
                renderItem={(d: any) => (
                  <List.Item>
                    <a onClick={() => navigate(`/hr/candidates/${d.id}`)}>
                      {d.candidate_number} — {d.full_name}
                    </a>{' '}
                    {d.match_on.map((m: string) => (
                      <Tag key={m}>same {m}</Tag>
                    ))}
                    {d.is_blacklisted ? <Tag color="red">Blacklisted</Tag> : null}
                  </List.Item>
                )}
              />
              {!isEdit && (
                <Checkbox checked={confirmDuplicate} onChange={(e) => setConfirmDuplicate(e.target.checked)}>
                  This is a different person — create anyway
                </Checkbox>
              )}
            </>
          }
        />
      )}
      <Form form={form} layout="vertical" initialValues={{ source: 'Walk-in', is_internal: false }} style={{ maxWidth: 960 }}>
        <Card title="Contact" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="first_name" label="First name" rules={[{ required: true, whitespace: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="last_name" label="Last name" rules={[{ required: true, whitespace: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Enter a valid email' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="+971 5x xxx xxxx" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="nationality" label="Nationality">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="current_location" label="Current location">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Background" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="current_title" label="Current job title">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="current_employer" label="Current employer">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="experience_years" label="Experience (years)">
                <InputNumber min={0} max={60} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item name="skills" label="Key skills">
                <Input placeholder="Comma-separated" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="tags_json" label="Tags" extra="Use tags for talent pools, e.g. 'Electrical', 'Arabic speaker'">
                <Select mode="tags" tokenSeparators={[',']} options={tagOptions.map((t) => ({ value: t, label: t }))} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Source" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="source" label="Source">
                <Select options={CANDIDATE_SOURCES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            {source === 'Referral' && (
              <Col xs={24} sm={12}>
                <Form.Item name="referral_employee_id" label="Referred by" rules={[{ required: true, message: 'Select the referring employee' }]}>
                  <RecruitPeoplePicker />
                </Form.Item>
              </Col>
            )}
            {source === 'Agency' && (
              <Col xs={24} sm={12}>
                <Form.Item name="agency_name" label="Agency" rules={[{ required: true, message: 'Agency name is required' }]}>
                  <Input />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={12}>
              <Form.Item name="is_internal" valuePropName="checked" label=" ">
                <Checkbox>Internal candidate (existing employee)</Checkbox>
              </Form.Item>
            </Col>
            {isInternal && (
              <Col xs={24} sm={12}>
                <Form.Item name="internal_employee_id" label="Employee record">
                  <RecruitPeoplePicker />
                </Form.Item>
              </Col>
            )}
            <Col xs={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title={isEdit ? 'Add CV' : 'CV'}>
          <Upload
            maxCount={1}
            beforeUpload={() => false}
            fileList={cv}
            onChange={({ fileList }) => setCv(fileList)}
            accept=".pdf,.doc,.docx"
          >
            <Button icon={<UploadOutlined />}>Choose CV (PDF / Word)</Button>
          </Upload>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default CandidateFormPage;
